import { Typography } from '@material-ui/core';
import {
  Download,
  Eye,
  Rewind,
  Shuffle,
  TrendingDown,
  TrendingUp
} from 'lucide-react';
import React from 'react';
import { DemoButton } from '../../components/DemoButton';
import { Headline } from '../../components/Headline';
import { Kicker } from '../../components/Kicker';
import { mobileLeadText } from '../../components/MarketingTypography';
import { Reveal } from '../../components/motion';
import {
  ContentTrendsMockup,
  HeatmapMockup,
  RevisionsMockup,
  SegmentationMockup
} from '../../components/OptimizeProductMockups';
import PageHead from '../../components/PageHead';
import { ProductIcon } from '../../components/ProductIcon';
import { SEO } from '../../components/SEO';
import MainLayout from '../../layouts/main';
import styled from '../../styled';
import { COLORS } from '../../themes/color';

const INK = '#0e1c2b';

const StorySection = styled('section')<{ flip?: boolean }>`
  max-width: 1100px;
  margin: 0 auto 80px;
  padding: 0 24px;
  display: grid;
  grid-template-columns: 1fr 1.15fr;
  gap: 64px;
  align-items: center;
  direction: ${(p) => (p.flip ? 'rtl' : 'ltr')};

  > * {
    direction: ltr;
  }

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 32px;
    direction: ltr;
  }
`;

const StoryVisual = styled('div')`
  min-width: 0;

  @media (max-width: 960px) {
    order: -1;
  }
`;

const StoryContent = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const StoryReveal = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
`;

const CenteredStoryReveal = styled(StoryReveal)`
  align-items: center;
  text-align: center;
`;

const StoryBody = styled('p')`
  font-size: 17px;
  line-height: 1.65;
  color: #4f4f4f;
  margin: 0;
  ${mobileLeadText}
`;

const FeaturesSection = styled('section')`
  max-width: 1100px;
  margin: 0 auto 80px;
  padding: 0 24px;
`;

const FeaturesHeader = styled('div')`
  text-align: center;
  max-width: 640px;
  margin: 0 auto 40px;
`;

const FeaturesIntro = styled('p')`
  font-size: 17px;
  line-height: 1.65;
  color: #64748b;
  margin: 0;
`;

const BentoGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;

  @media (max-width: 960px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const BentoCard = styled('div')`
  height: 100%;
  box-sizing: border-box;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 28px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition: box-shadow 0.2s, border-color 0.2s, transform 0.2s;

  &:hover {
    border-color: ${COLORS.PRIMARY};
    box-shadow: 0 8px 30px rgba(46, 92, 255, 0.1);
    transform: translateY(-2px);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover {
      transform: none;
    }
  }
`;

const BentoTitle = styled('h3')`
  font-size: 17px;
  font-weight: 700;
  color: ${INK};
  margin: 0;
`;

const BentoBody = styled('p')`
  font-size: 14px;
  line-height: 1.6;
  color: #64748b;
  margin: 0;
`;

const FinalCta = styled('div')`
  text-align: center;
  max-width: 640px;
  margin: 0 auto 120px;
  padding: 0 24px;
`;

const FinalCtaBody = styled('p')`
  font-size: 17px;
  line-height: 1.65;
  color: #64748b;
  margin: 0;
  ${mobileLeadText}
`;

const BENTO_FEATURES = [
  {
    icon: Eye,
    title: 'Find hidden opportunities with impression tracking',
    body:
      'See which links are rarely scrolled into view but convert when they are, then move them where readers will actually find them.'
  },
  {
    icon: TrendingUp,
    title: 'Catch trending and seasonal content early',
    body:
      'The Growth report surfaces pages suddenly gaining clicks or revenue, so you can update them while demand is high.'
  },
  {
    icon: Download,
    title: 'Export and cross-reference your analytics',
    body:
      'Export page-level revenue analytics to combine with data from SEO tools or your display ad network.'
  },
  {
    icon: TrendingDown,
    title: 'Stop revenue drops from broken links or stock issues',
    body:
      'Identify revenue drops quickly and replace broken links or out-of-stock product recommendations before they cost you.'
  },
  {
    icon: Rewind,
    title: 'Trace metric changes back to content edits',
    body:
      'Revision tracking connects moves in CTR and RPM to the specific edit that caused them.'
  },
  {
    icon: Shuffle,
    title: 'Compare performance between periods',
    body:
      'Every content metric compares against the previous period automatically, so you can see where growth is coming from.'
  }
] as const;

export default () => {
  return (
    <MainLayout>
      <div>
        <SEO
          pathname="/product/optimize/"
          title="Affilimate Optimize: Content Optimization for Editorial Teams"
          description="The content optimization solution for editorial teams: on-page analytics and heatmaps, offsite and email tracking, custom dimensions, paid marketing integrations, and syndication support."
        />
        <PageHead>
          <Reveal>
            <Headline
              variant="h1"
              gutterBottom
              component="h1"
              style={{
                maxWidth: '800px',
                margin: '0 auto 24px',
                fontWeight: 900,
                display: 'block'
              }}
            >
              Content optimization for editorial and business teams
            </Headline>
            <Typography
              variant="body1"
              color="textSecondary"
              style={{ maxWidth: '620px', margin: '24px auto 12px' }}
            >
              Earn more from the content you already publish. Detailed,
              in-article analytics let you trace performance from traffic source
              or paid marketing channel to the exact placement that converted.
            </Typography>
            <div style={{ textAlign: 'center', margin: '48px auto 0' }}>
              <DemoButton text="Get a demo" variant="contained" />
            </div>
          </Reveal>
        </PageHead>

        <StorySection>
          <StoryContent>
            <Reveal>
              <StoryReveal>
                <Kicker style={{ marginBottom: 0 }}>Heatmaps</Kicker>
                <Headline component="h2" variant="h3" style={{ margin: 0 }}>
                  Affiliate revenue attribution down to the pixel
                </Headline>
                <StoryBody>
                  See exactly which affiliate links, buttons, images, and
                  calls-to-action drive conversions on each page, and which get
                  scrolled past.
                </StoryBody>
                <StoryBody>
                  Color-coded, link-level metrics anyone on the team can turn
                  into an action plan: better placements, better retailer picks,
                  and the evidence to negotiate higher commission rates.
                </StoryBody>
              </StoryReveal>
            </Reveal>
          </StoryContent>
          <StoryVisual>
            <Reveal delay={150}>
              <HeatmapMockup />
            </Reveal>
          </StoryVisual>
        </StorySection>

        <StorySection flip>
          <StoryContent>
            <Reveal>
              <StoryReveal>
                <Kicker style={{ marginBottom: 0 }}>Content trends</Kicker>
                <Headline component="h2" variant="h3" style={{ margin: 0 }}>
                  Monitor your content portfolio for revenue spikes and drops
                </Headline>
                <StoryBody>
                  Find which content and products generate the most revenue
                  across affiliate programs, with pageviews, clicks, CTR, RPM,
                  and earnings for every page in one report.
                </StoryBody>
                <StoryBody>
                  Sort your portfolio by fastest growing or declining articles,
                  so you know which pages need an update today.
                </StoryBody>
              </StoryReveal>
            </Reveal>
          </StoryContent>
          <StoryVisual>
            <Reveal delay={150}>
              <ContentTrendsMockup />
            </Reveal>
          </StoryVisual>
        </StorySection>

        <StorySection>
          <StoryContent>
            <Reveal>
              <StoryReveal>
                <Kicker style={{ marginBottom: 0 }}>
                  Content segmentation
                </Kicker>
                <Headline component="h2" variant="h3" style={{ margin: 0 }}>
                  Create more profitable content from your top segments
                </Headline>
                <StoryBody>
                  Benchmark performance between content formats, product
                  categories, target audiences, and more. See which segments
                  drive the most revenue, or carry the highest EPCs and RPMs.
                </StoryBody>
                <StoryBody>
                  Create custom content types and tags, and apply them across
                  your portfolio without changing a single link.
                </StoryBody>
              </StoryReveal>
            </Reveal>
          </StoryContent>
          <StoryVisual>
            <Reveal delay={150}>
              <SegmentationMockup />
            </Reveal>
          </StoryVisual>
        </StorySection>

        <StorySection flip>
          <StoryContent>
            <Reveal>
              <StoryReveal>
                <Kicker style={{ marginBottom: 0 }}>Revision tracking</Kicker>
                <Headline component="h2" variant="h3" style={{ margin: 0 }}>
                  Track the impact of content updates on affiliate revenue
                </Headline>
                <StoryBody>
                  Track how editorial changes affect affiliate revenue,
                  click-through rate, RPM, and EPC on pages across your site,
                  without setting up experiments by hand.
                </StoryBody>
                <StoryBody>
                  Revisions are created automatically, fit your existing
                  editorial workflow, and scale across your entire portfolio of
                  content.
                </StoryBody>
              </StoryReveal>
            </Reveal>
          </StoryContent>
          <StoryVisual>
            <Reveal delay={150}>
              <RevisionsMockup />
            </Reveal>
          </StoryVisual>
        </StorySection>

        <FeaturesSection>
          <Reveal>
            <FeaturesHeader>
              <CenteredStoryReveal>
                <Kicker style={{ marginBottom: 0 }}>Editorial toolkit</Kicker>
                <Headline component="h2" style={{ margin: 0 }}>
                  More ways to put content insights to work
                </Headline>
                <FeaturesIntro>
                  Optimize is built for the daily work of running a commerce
                  content operation: finding what to fix, fixing it, and proving
                  the fix worked.
                </FeaturesIntro>
              </CenteredStoryReveal>
            </FeaturesHeader>
          </Reveal>
          <BentoGrid>
            {BENTO_FEATURES.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Reveal
                  key={feature.title}
                  delay={index * 60}
                  style={{ height: '100%' }}
                >
                  <BentoCard>
                    <ProductIcon>
                      <Icon size={20} />
                    </ProductIcon>
                    <BentoTitle>{feature.title}</BentoTitle>
                    <BentoBody>{feature.body}</BentoBody>
                  </BentoCard>
                </Reveal>
              );
            })}
          </BentoGrid>
        </FeaturesSection>

        <FinalCta>
          <Reveal>
            <CenteredStoryReveal>
              <Headline component="h2" style={{ margin: 0 }}>
                See Optimize on live content
              </Headline>
              <FinalCtaBody>
                Walk through heatmaps, content trends, segmentation, and
                revision tracking on a demo featuring live content and actual
                performance.
              </FinalCtaBody>
              <DemoButton text="Get a demo" variant="contained" />
            </CenteredStoryReveal>
          </Reveal>
        </FinalCta>
      </div>
    </MainLayout>
  );
};
